import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AdminNotification,
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "../services/notificationService";

const POLL_INTERVAL_MS = 30_000;

const severityColor = (severity: AdminNotification["severity"]) => {
  switch (severity) {
    case "warning":
      return "#ed6c02";
    case "info":
      return "#0288d1";
    case "error":
    default:
      return "#d32f2f";
  }
};

const SeverityIcon: React.FC<{ severity: AdminNotification["severity"] }> = ({
  severity,
}) => {
  const color = severityColor(severity);
  if (severity === "warning") return <WarningAmberIcon sx={{ color }} fontSize="small" />;
  if (severity === "info") return <InfoOutlinedIcon sx={{ color }} fontSize="small" />;
  return <ErrorOutlineIcon sx={{ color }} fontSize="small" />;
};

const formatRelativeTime = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
};

const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const queryClient = useQueryClient();

  // Lightweight badge poll — runs whether or not the popover is open.
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["admin-notifications-unread-count"],
    queryFn: fetchUnreadCount,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  // Full list — only fetched (and polled) while the popover is open.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-notifications-list"],
    queryFn: () => fetchNotifications("all", 20, 1),
    enabled: open,
    refetchInterval: open ? POLL_INTERVAL_MS : false,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-notifications-unread-count"] });
    queryClient.invalidateQueries({ queryKey: ["admin-notifications-list"] });
  };

  const markReadMutation = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: invalidate,
  });

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidate,
  });

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = (notification: AdminNotification) => {
    if (!notification.read) {
      markReadMutation.mutate(notification._id);
    }
  };

  const notifications = data?.notifications ?? [];

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={handleOpen} size="large">
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 380, maxWidth: "90vw" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <Button
            size="small"
            disabled={unreadCount === 0 || markAllMutation.isPending}
            onClick={() => markAllMutation.mutate()}
          >
            Mark all read
          </Button>
        </Box>
        <Divider />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Couldn't load notifications.
            </Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              You're all caught up.
            </Typography>
          </Box>
        ) : (
          <List
            sx={{ maxHeight: 420, overflowY: "auto", py: 0 }}
            disablePadding
          >
            {notifications.map((notification) => (
              <ListItemButton
                key={notification._id}
                onClick={() => handleNotificationClick(notification)}
                alignItems="flex-start"
                sx={{
                  bgcolor: notification.read ? "transparent" : "action.hover",
                  borderLeft: `3px solid ${
                    notification.read
                      ? "transparent"
                      : severityColor(notification.severity)
                  }`,
                  py: 1.25,
                }}
              >
                <Box sx={{ mr: 1.5, mt: 0.25 }}>
                  <SeverityIcon severity={notification.severity} />
                </Box>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: notification.read ? 400 : 600 }}
                    >
                      {notification.title}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                        sx={{ display: "block" }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        component="span"
                      >
                        {formatRelativeTime(notification.createdAt)}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
